import { isEmpty } from 'lodash-es'
import { financialCalculatorService } from '../services/financialCalculator.service'
import clientLogger from 'ppbl/utils/client-logger'

// eslint-disable-next-line max-statements
export const calculateSelectedOfferValue = (
  opportunityId,
  maximumLoanAmount,
  termInWeeks,
  desiredAmount,
  customOfferConstraints,
  averageMonthlyGrossSales,
  weeklyInterestRate,
) => {
  if (!maximumLoanAmount) {
    clientLogger.error(
      `maxAmount is undefined or null for opportunityId: ${opportunityId}`,
    )
    return {}
  }
  if (!termInWeeks) {
    clientLogger.error(
      `term is undefined or null for opportunityId: ${opportunityId}`,
    )
    return {}
  }
  if (!desiredAmount) {
    clientLogger.error(
      `desiredAmount is undefined or null for opportunityId: ${opportunityId}`,
    )
    return {}
  }
  const averageGrossScore = averageMonthlyGrossSales?.value || 0
  const offers = customOfferConstraints
  const values = {}

  if (weeklyInterestRate) {
    values.weeklyRate = weeklyInterestRate
    values.weeklyInterestRate = weeklyInterestRate
  } else {
    values.weeklyRate = financialCalculatorService.getWeeklyPriceByOffer(
      termInWeeks,
      offers,
    )
  }

  values.offerAmount = desiredAmount
  values.termInWeeks = termInWeeks
  values.monthlyRevenue = Number(averageGrossScore)
  values.weeklyRevenue = financialCalculatorService.getWeeklyRevenue(
    values.monthlyRevenue,
  )

  values.weeklyPayment = financialCalculatorService.getWeeklyTotalPayment(
    values.offerAmount,
    values.termInWeeks,
    values.weeklyRate,
  )
  values.weeklyPrincipalPayment =
    financialCalculatorService.getWeeklyPrincipalPayment(
      values.offerAmount,
      values.termInWeeks,
    )

  values.weeklyInterestPayment =
    financialCalculatorService.getWeeklyInterestPayment(
      values.offerAmount,
      values.weeklyRate,
    )

  values.centsOnTheDollar = financialCalculatorService.getCentsOnTheDollar(
    values.offerAmount,
    values.termInWeeks,
    values.weeklyRate,
  )

  values.financeCharges = financialCalculatorService.getTotalFinanceCharges(
    values.offerAmount,
    values.termInWeeks,
    values.weeklyRate,
  )
  values.totalRepayment = financialCalculatorService.getTotalAmountOfPayments(
    values.offerAmount,
    values.termInWeeks,
    values.weeklyRate,
  )

  values.annualInterestRate = financialCalculatorService.getAnnualInterestRate(
    offers,
    values.weeklyRate,
    values.termInWeeks,
  )

  values.totalInterestPercentage =
    financialCalculatorService.getTotalInterestPercent(
      values.termInWeeks,
      values.weeklyRate,
    )

  values.totalInterestCharge =
    financialCalculatorService.getTotalInterestCharge(
      values.termInWeeks,
      values.weeklyInterestPayment,
    )

  values.termInMonths = financialCalculatorService.getTermInMonths(
    values.termInWeeks,
  )
  values.maxProportionOfRevenue =
    financialCalculatorService.getMaxProportionOfRevenue(
      values.offerAmount,
      values.termInWeeks,
      values.weeklyRevenue,
    )

  values.isQualified = financialCalculatorService.isQualified(
    values.offerAmount,
    values.termInWeeks,
    values.maxProportionOfRevenue,
    maximumLoanAmount,
    offers,
  )

  values.frsaSplitPercentage =
    financialCalculatorService.getFRSASplitPercentage(
      values.totalRepayment,
      values.weeklyRevenue,
      values.termInWeeks,
    )
  values.annualPercentageRate =
    financialCalculatorService.getAnnualPercentageRate(
      offers,
      values.weeklyRate,
      values.termInWeeks,
    )
  values.purchaseAmount = financialCalculatorService.getPurchaseAmount(
    values.offerAmount,
    values.totalInterestCharge,
  )

  values.dailyCollectionAmount =
    financialCalculatorService.getDailyCollectionAmount(
      values.purchaseAmount,
      values.termInMonths,
    )

  values.dailyCollectionProportionOfRevenue =
    values.dailyCollectionAmount && values.monthlyRevenue
      ? financialCalculatorService.getDailyCollectionProportionOfRevenue(
          values.dailyCollectionAmount,
          values.monthlyRevenue,
        )
      : 0

  values.weeklyCollectionAmount =
    financialCalculatorService.getWeeklyCollectionAmount(
      values.purchaseAmount,
      values.termInWeeks,
    )

  values.weeklyCollectionProportionOfRevenue =
    values.weeklyCollectionAmount && values.monthlyRevenue
      ? financialCalculatorService.getWeeklyCollectionProportionOfRevenue(
          values.weeklyCollectionAmount,
          values.monthlyRevenue,
        )
      : 0

  try {
    const offer = financialCalculatorService.getOfferByTerm(
      offers,
      values.termInWeeks,
    )

    if (!offer) {
      clientLogger.error(`No offer found for termInWeeks ${values.termInWeeks}`)
      return values
    }

    values.maxAutomatedOfferAmount = offer.maxAutomatedOfferAmount
    values.maxAmount = offer.maxAmount.value

    // if max automated amount is greater than max amount, set max amount for maxAutoQualifiedAmount
    let maxAutoQualifiedAmount = offer.maxAutoQualifiedAmount.value
    if (Number(maxAutoQualifiedAmount) > Number(offer.maxAmount.value)) {
      maxAutoQualifiedAmount = offer.maxAmount.value
    }

    values.maxAutoQualifiedAmount = maxAutoQualifiedAmount
    values.maxEstimatedVerifiedAutomatedOfferAmount =
      offer.maxEstimatedVerifiedAutomatedOfferAmount

    if (!values.weeklyInterestRate) {
      values.weeklyInterestRate = offer.weeklyInterestRate
    }
  } catch (err) {
    clientLogger.error(`Error on calculateValues: ${err.message}`, err)
  }
  return values
}

export const isApplicationAutomationEligible = (tags) => {
  return tags && tags[0] === 'AUTOMATICALLY_QUALIFIED' ? 'Y' : 'N'
}

export const isLoanAutomationQualified = (
  tags,
  desiredAmount,
  maxAutomatedAmount,
) => {
  return (
    tags &&
    tags[0] === 'AUTOMATICALLY_QUALIFIED' &&
    Number(desiredAmount) <= Number(maxAutomatedAmount)
  )
}

export const convertTermToWeeks = (termInDays) => {
  if (isNaN(termInDays)) {
    return null
  }

  const weeks = termInDays / 7

  if (weeks % 1 !== 0) {
    return null
  }

  return weeks
}

export const roundOffNumberValue = (value) => Number(value?.toFixed(2))

export const getWeeklyPercentage = (loanFee, offerAmount, termInWeeks) => {
  if (isNaN(termInWeeks) || isNaN(loanFee) || isNaN(offerAmount)) {
    return null
  }

  const weeklyInterestPercentage = loanFee / offerAmount / termInWeeks

  return weeklyInterestPercentage
}

export const getLoanFee = (weeklyInterestRate, termInWeeks, offerAmount) => {
  if (isNaN(weeklyInterestRate) || isNaN(termInWeeks)) {
    return null
  }

  const loanFee = (Number(offerAmount) * weeklyInterestRate * termInWeeks) / 100

  return Number(loanFee)
}

export const getNumberFromString = (string) => {
  if (!string) {
    return null
  }

  const matches = string
    .match(/[\d,]+(\.\d+)?/g)
    .map((num) => num.replace(/,/g, ''))

  if (isEmpty(matches)) {
    return null
  }

  return matches[0]
}
